import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import type { ActionList, Group, GroupCreate, GroupList, GroupPermissions, PageList } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, Field, Modal, PageHeader, cn } from '../../components/ui';

type Flags = { can_view: boolean; can_create: boolean; can_edit: boolean; can_delete: boolean };

export function PermissionsPage() {
  const { t, i18n } = useTranslation();
  const { canAction } = useAuth();
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ['groups'], queryFn: () => api.get<GroupList>('/api/groups') });
  const pages = useQuery({ queryKey: ['pages'], queryFn: () => api.get<PageList>('/api/pages') });
  const actions = useQuery({ queryKey: ['actions'], queryFn: () => api.get<ActionList>('/api/actions') });
  const [selected, setSelected] = useState<string | null>(null);
  const perms = useQuery({ queryKey: ['group-permissions', selected], queryFn: () => api.get<GroupPermissions>(`/api/groups/${selected}/permissions`), enabled: Boolean(selected) });
  const [pageState, setPageState] = useState<Record<string, Flags>>({});
  const [actionState, setActionState] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<GroupCreate>({ code: '', name_en: '', name_ar: '', colour: '#0f766e' });
  const editable = canAction('permissions.manage');

  useEffect(() => {
    if (!perms.data) return;
    setPageState(Object.fromEntries(perms.data.pages.map((p) => [p.page_code, { can_view: p.can_view, can_create: p.can_create, can_edit: p.can_edit, can_delete: p.can_delete }])));
    setActionState(new Set(perms.data.actions));
    setDirty(false);
  }, [perms.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put<GroupPermissions>(`/api/groups/${selected}/permissions`, {
        pages: Object.entries(pageState).filter(([, f]) => f.can_view).map(([page_code, f]) => ({ page_code, ...f })),
        actions: [...actionState],
      }),
    onSuccess: () => {
      toast.success(t('permissions.saved'));
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ['group-permissions', selected] });
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const create = useMutation({
    mutationFn: (body: GroupCreate) => api.post<Group>('/api/groups', body),
    onSuccess: (g) => {
      toast.success(t('permissions.groupCreated'));
      setCreating(false);
      void qc.invalidateQueries({ queryKey: ['groups'] });
      setSelected(g.code);
    },
    onError: (e) => toast.error(describeError(e, t)),
  });

  const toggle = (code: string, key: keyof Flags) => {
    const row = pageState[code] ?? { can_view: false, can_create: false, can_edit: false, can_delete: false };
    const next = { ...row, [key]: !row[key] };
    if (key !== 'can_view' && next[key]) next.can_view = true;
    if (key === 'can_view' && !next.can_view) Object.assign(next, { can_create: false, can_edit: false, can_delete: false });
    setPageState({ ...pageState, [code]: next });
    setDirty(true);
  };
  const toggleAction = (code: string) => {
    const next = new Set(actionState);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setActionState(next);
    setDirty(true);
  };

  const sections = new Map<string, typeof pages.data extends { items: infer I } ? I : never>();
  for (const p of pages.data?.items ?? []) sections.set(p.category, [...(sections.get(p.category) ?? []), p] as never);

  return (
    <div>
      <PageHeader title={t('permissions.title')} actions={editable && <button type="button" className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> {t('permissions.newGroup')}</button>} />
      <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <div className="card p-2">
          <div className="label px-2">{t('permissions.groups')}</div>
          {(groups.data?.items ?? []).map((g) => (
            <button key={g.code} type="button" onClick={() => setSelected(g.code)} className={cn('flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-start text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800', selected === g.code && 'bg-brand-50 dark:bg-brand-700/30')}>
              <span className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: g.colour ?? '#888' }} />
                <span dir="auto">{pick(i18n.language, g.name_en, g.name_ar)}</span>
              </span>
              <span className="flex items-center gap-1 text-xs text-neutral-500">{g.member_count}{g.is_system && <Badge>{t('permissions.system')}</Badge>}</span>
            </button>
          ))}
        </div>
        <div className="card p-4">
          {!selected && <p className="text-sm text-neutral-500">{t('permissions.selectGroup')}</p>}
          {selected && perms.data && (
            <>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold">{selected}</h2>
                {editable && <button type="button" className="btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{t('app.save')}</button>}
              </div>
              <h3 className="label">{t('permissions.pages')}</h3>
              <table className="mb-4 w-full text-sm">
                <thead><tr className="text-xs uppercase text-neutral-500"><th className="py-1 text-start"></th>{(['view', 'create', 'edit', 'delete'] as const).map((k) => <th key={k} className="w-16 py-1">{t(`permissions.${k}`)}</th>)}</tr></thead>
                <tbody>
                  {[...sections.entries()].map(([section, items]) => (
                    <SectionRows key={section} section={section} items={items as PageList['items']} state={pageState} editable={editable} onToggle={toggle} />
                  ))}
                </tbody>
              </table>
              <h3 className="label">{t('permissions.actions')}</h3>
              <div className="grid gap-1 sm:grid-cols-2">
                {(actions.data?.items ?? []).map((a) => (
                  <label key={a.code} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" disabled={!editable} checked={actionState.has(a.code)} onChange={() => toggleAction(a.code)} />
                    <span dir="auto">{pick(i18n.language, a.name_en, a.name_ar)}</span>
                    <span className="text-xs text-neutral-400">{a.code}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title={t('permissions.newGroup')} footer={<><button type="button" className="btn-secondary" onClick={() => setCreating(false)}>{t('app.cancel')}</button><button type="button" className="btn-primary" onClick={() => create.mutate(form)} disabled={create.isPending}>{t('app.create')}</button></>}>
        <div className="grid gap-3">
          <Field label={t('permissions.code')} required hint={t('permissions.codeHint')}><input className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })} /></Field>
          <Field label={t('users.nameEn')} required><input className="input" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></Field>
          <Field label={t('users.nameAr')} required><input className="input" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></Field>
          <Field label={t('permissions.colour')}><input className="input h-10 w-20 p-1" type="color" value={form.colour ?? '#0f766e'} onChange={(e) => setForm({ ...form, colour: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}

function SectionRows({ section, items, state, editable, onToggle }: { section: string; items: PageList['items']; state: Record<string, Flags>; editable: boolean; onToggle: (code: string, key: keyof Flags) => void }) {
  const { i18n } = useTranslation();
  return (
    <>
      <tr><td colSpan={5} className="pt-2 text-xs font-semibold uppercase text-neutral-400">{section}</td></tr>
      {items.map((p) => (
        <tr key={p.code} className="border-t border-neutral-100 dark:border-neutral-800">
          <td className="py-1" dir="auto">{pick(i18n.language, p.name_en, p.name_ar)} <span className="text-xs text-neutral-400">{p.code}</span></td>
          {(['can_view', 'can_create', 'can_edit', 'can_delete'] as const).map((k) => <td key={k} className="py-1 text-center"><input type="checkbox" disabled={!editable} checked={Boolean(state[p.code]?.[k])} onChange={() => onToggle(p.code, k)} /></td>)}
        </tr>
      ))}
    </>
  );
}
